import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  FileText,
  Sparkles,
  Calendar,
  Target,
  BookOpen,
  CheckCircle2,
  Plus,
  Library,
  History,
  Building2,
  ArrowRight,
  ChevronUp
} from 'lucide-react'
import { KPAData } from '@/types/evidence'
import ImprovedObjectiveWizard from './objective-setting/ImprovedObjectiveWizard'
import TemplateObjectiveSelectionDialog from './objectives/TemplateObjectiveSelectionDialog'
import { ObjectiveTemplateObjective } from '@/services/api/templateService'

interface ObjectiveTemplate {
  id: string
  title: string
  description: string
  kpaId: string
  suggestedMetrics: string[]
  difficulty: 'easy' | 'medium' | 'hard'
}

interface PriorYearObjective {
  id: number
  title: string
  description: string
  progress: number
  yearAchieved: number
  kpaName: string
}

interface ObjectiveSettingDialogProps {
  open: boolean
  onClose: () => void
  kpas: KPAData[]
  templates?: ObjectiveTemplate[]
  priorYearObjectives?: PriorYearObjective[]
  onCreateObjective: (objective: {
    title: string
    description: string
    kpaId: string
    strategicObjectiveId?: number
    startDate: string
    endDate: string
    metrics?: string[]
  }) => void
}

export default function ObjectiveSettingDialog({
  open,
  onClose,
  kpas,
  templates = [],
  priorYearObjectives = [],
  onCreateObjective
}: ObjectiveSettingDialogProps) {
  const [allocationSource, setAllocationSource] = useState<'scratch' | 'template' | 'prior_year' | 'institutional' | null>(null)
  const [showObjectiveWizard, setShowObjectiveWizard] = useState(false)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [selectedTemplateObjectives, setSelectedTemplateObjectives] = useState<ObjectiveTemplateObjective[]>([])

  const handleAllocateObjectives = async (objectives: any[]) => {
    // Handle objective creation
    if (objectives.length > 0) {
      onCreateObjective(objectives[0])
    }
    setShowObjectiveWizard(false)
    setAllocationSource(null)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-br from-heritage-blue to-future-blue flex-shrink-0">
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
            <Target className="h-6 w-6" />
            Create New Objective
          </DialogTitle>
          <DialogDescription className="text-blue-50">
            You can now proceed with objective creation. Select your preferred objective source below.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 h-full">
          <div className="px-6 pb-6">
            {!allocationSource ? (
              <>
                <Card className="mt-4">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <div className="flex-1">
                        <CardTitle className="text-lg">Collaboration Completed</CardTitle>
                        <CardDescription className="text-sm">
                          Choose how you want to create your objectives
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* 4 Source Options */}
                    <div className="grid grid-cols-2 gap-4">

                      {/* Option 1: Create from Scratch */}
                      <Card 
                        className="border-2 hover:border-heritage-blue cursor-pointer transition-all hover:shadow-md"
                        onClick={() => {
                          setAllocationSource('scratch')
                          setShowObjectiveWizard(true)
                        }}
                      >
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Plus className="h-5 w-5 text-heritage-blue" />
                            {/* OLD: Create From Scratch */}
                            Create Objective from Scratch
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            Build custom objectives with KPIs, timelines, and SMART analysis
                          </p>
                          <div className="mt-4 flex items-center text-sm text-heritage-blue font-medium">
                            Start Creating
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Option 2: From Template Library */}
                      <Card 
                        className="border-2 hover:border-purple-500 cursor-pointer transition-all hover:shadow-md"
                        onClick={() => {
                          setAllocationSource('template')
                          setShowTemplateDialog(true)
                        }}
                      >
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-purple-500" />
                            HR & People Leader Templates
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            Pre-configured by HR/People Leader, delivered via multiple methods
                          </p>
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="secondary" className="text-xs">Chat</Badge>
                              <Badge variant="secondary" className="text-xs">Email</Badge>
                              <Badge variant="secondary" className="text-xs">Document</Badge>
                              <Badge variant="secondary" className="text-xs">Meeting</Badge>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center text-sm text-purple-500 font-medium">
                            Browse Templates
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Option 3: From Prior Year */}
                      <Card 
                        className="border-2 hover:border-amber-500 cursor-pointer transition-all hover:shadow-md"
                        onClick={() => setAllocationSource('prior_year')}
                      >
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <History className="h-5 w-5 text-amber-600" />
                            Prior Year Objectives
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            Carry forward and adapt objectives from your previous impact & growth cycle
                          </p>
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">
                              Available objectives:
                            </p>
                            <div className="flex gap-3 mt-1">
                              <span className="text-xs">Completed: <span className="font-semibold text-emerald-600">{priorYearObjectives.filter(o => o.progress === 100).length}</span></span>
                              <span className="text-xs">In progress: <span className="font-semibold text-amber-600">{priorYearObjectives.filter(o => o.progress < 100).length}</span></span>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center text-sm text-amber-600 font-medium">
                            View Prior Objectives
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Option 4: Institutional Library */}
                      <Card 
                        className="border-2 hover:border-emerald-500 cursor-pointer transition-all hover:shadow-md"
                        onClick={() => setAllocationSource('institutional')}
                      >
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Library className="h-5 w-5 text-emerald-600" />
                            Institutional Library
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            Browse proven objectives used across the institution
                          </p>
                          <div className="mt-2">
                            <div className="flex items-center gap-1 text-xs text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>Aligned with institutional goals</span>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center text-sm text-emerald-600 font-medium">
                            View Strategic Goals
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : allocationSource === 'scratch' && showObjectiveWizard ? (
              <div className="space-y-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAllocationSource(null)
                    setShowObjectiveWizard(false)
                  }}
                  className="mb-4"
                >
                  <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
                  Back to Objective Sources
                </Button>
                <ImprovedObjectiveWizard
                  onSave={handleAllocateObjectives}
                  onCancel={() => {
                    setAllocationSource(null)
                    setShowObjectiveWizard(false)
                  }}
                />
              </div>
            ) : allocationSource === 'template' && selectedTemplateObjectives.length > 0 ? (
              <div className="space-y-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAllocationSource(null)
                    setSelectedTemplateObjectives([])
                  }}
                  className="mb-4"
                >
                  <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
                  Back to Objective Sources
                </Button>
                <Card>
                  <CardHeader>
                    <CardTitle>Selected Template Objectives</CardTitle>
                    <CardDescription>
                      {selectedTemplateObjectives.length} objective{selectedTemplateObjectives.length !== 1 ? 's' : ''} ready to be created
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedTemplateObjectives.map((obj, idx) => (
                      <Card key={idx} className="p-4">
                        <h4 className="font-medium">{obj.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{obj.description}</p>
                        {obj.kpaName && (
                          <Badge className="mt-2" variant="outline">{obj.kpaName}</Badge>
                        )}
                      </Card>
                    ))}
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => {
                          // Convert template objectives to the format expected by onCreateObjective
                          // For now, just handle the first one
                          if (selectedTemplateObjectives.length > 0) {
                            const obj = selectedTemplateObjectives[0]
                            handleAllocateObjectives([obj])
                          }
                        }}
                        className="flex-1"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Create {selectedTemplateObjectives.length} Objective{selectedTemplateObjectives.length !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="mt-4">
                <CardContent className="py-12 text-center">
                  <div className="text-muted-foreground">
                    <p className="mb-2">This feature is coming soon</p>
                    <Button
                      variant="outline"
                      onClick={() => setAllocationSource(null)}
                    >
                      <ChevronUp className="h-4 w-4 mr-2 rotate-180" />
                      Back to Objective Sources
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => {
              setAllocationSource(null)
              setShowObjectiveWizard(false)
              onClose()
            }}
          >
            Close
          </Button>
        </div>

        {/* Template Selection Dialog */}
        <TemplateObjectiveSelectionDialog
          open={showTemplateDialog}
          onOpenChange={(open) => {
            setShowTemplateDialog(open)
            if (!open && selectedTemplateObjectives.length === 0) {
              setAllocationSource(null)
            }
          }}
          onObjectivesSelected={(objectives, templateInfo) => {
            setSelectedTemplateObjectives(objectives)
            setShowTemplateDialog(false)
          }}
          onBack={() => {
            setShowTemplateDialog(false)
            setAllocationSource(null)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
